/**
 * Pure SIWE link / sign-in helpers used by `useWallet`.
 *
 * These functions decide the outcome of "link the current wagmi-active
 * wallet into the Privy session" based solely on the state passed in as
 * `deps` — no React, no hooks, no module-level side effects. They live
 * here rather than inline in `use-wallet.ts` so they can be unit-tested
 * without standing up the full Privy + wagmi provider tree.
 *
 * See the end of `hooks/__tests__/attempt-link-active-wallet.test.ts`
 * and `attempt-sign-in-active-wallet.test.ts` for the decision-matrix
 * tests that pin every branch of this module.
 */

/**
 * Discriminated result of {@link attemptLinkActiveWallet} /
 * {@link attemptSignInActiveWallet}.
 *
 * `wallet-claimed` is the Privy "linked_to_another_user" /
 * "User already exists for this address" error: the wagmi-active
 * wallet is associated with a different Privy user, so SIWE-linking
 * it into the current session would violate the one-user-per-wallet
 * constraint. The user has to sign out and back in with that wallet
 * (or unlink it from the other account) — there's no programmatic fix.
 */
export type LinkActiveWalletResult =
  | { ok: true; reason: 'already-linked' | 'linked' }
  | { ok: false; reason: 'not-ready' | 'wallet-not-found' | 'dismissed' | 'wallet-claimed' };

/** Minimal shape of a `useWallets()` entry that the attempt functions touch. */
export interface LinkableWallet {
  address: string;
  loginOrLink: () => Promise<unknown>;
  /**
   * Optional EIP-1193 provider accessor. When present, we use it to
   * confirm — RIGHT BEFORE asking the wallet to sign — that the
   * provider's currently-active account list still contains the
   * address we intend to sign with. This closes the window where
   * Privy's `useWallets()` entry has updated to the wagmi-active
   * address but the underlying injected provider hasn't yet (or is
   * out of sync because the user switched accounts inside the wallet
   * UI directly). Without this check, `loginOrLink()` would silently
   * have the provider sign with whatever it considers active — the
   * "signed in with the old wallet" symptom.
   */
  getEthereumProvider?: () => Promise<{
    request: (args: { method: string }) => Promise<unknown>;
  }>;
}

/**
 * Match Privy errors that mean "this wallet is already linked to a
 * different Privy user". Privy's React-Auth SDK doesn't publish a
 * stable error code for this, so we string-match the same family of
 * messages the SDK surfaces: the modal copy ("User already exists
 * for this address"), the API error code (`linked_to_another_user`),
 * and historical phrasing. Case-insensitive so we don't fight casing
 * changes in future SDK releases.
 */
export function isWalletClaimedError(err: unknown): boolean {
  const msg =
    typeof err === 'string'
      ? err
      : (err as { message?: string } | null)?.message ?? '';
  if (!msg) return false;
  const lower = msg.toLowerCase();
  return (
    lower.includes('linked_to_another_user') ||
    lower.includes('already linked to another') ||
    lower.includes('already exists') ||
    lower.includes('belongs to another')
  );
}

/**
 * Provider-level sync check used by both attempt functions. Returns
 * `true` when the EIP-1193 provider's account list confirms the
 * target address; `false` when a different account is active.
 * Errors and absent providers fall through to `true` — an
 * inconclusive read isn't a reason to block the sign request.
 */
async function providerMatchesAddress(
  target: LinkableWallet,
  normalizedAddress: string,
): Promise<boolean> {
  if (!target.getEthereumProvider) return true;
  try {
    const provider = await target.getEthereumProvider();
    const accounts = (await provider.request({ method: 'eth_accounts' })) as
      | string[]
      | null
      | undefined;
    const lowered = (accounts ?? []).map((a) => a.toLowerCase());
    if (lowered.length > 0 && !lowered.includes(normalizedAddress)) {
      return false;
    }
    return true;
  } catch {
    // Provider read failures aren't conclusive — fall through.
    return true;
  }
}

/**
 * SIWE-link the wagmi-active wallet into the CURRENT Privy session.
 * Pure, side-effect-free decision tree. Returns one of the discriminated
 * outcomes in {@link LinkActiveWalletResult}.
 */
export async function attemptLinkActiveWallet(deps: {
  walletsReady: boolean;
  authenticated: boolean;
  wagmiAddress: string | undefined;
  linkedAddresses: Set<string>;
  wallets: ReadonlyArray<LinkableWallet>;
}): Promise<LinkActiveWalletResult> {
  if (!deps.walletsReady || !deps.authenticated || !deps.wagmiAddress) {
    return { ok: false, reason: 'not-ready' };
  }
  const normalized = deps.wagmiAddress.toLowerCase();
  if (deps.linkedAddresses.has(normalized)) {
    return { ok: true, reason: 'already-linked' };
  }
  const target = deps.wallets.find((w) => w.address.toLowerCase() === normalized);
  if (!target) {
    return { ok: false, reason: 'wallet-not-found' };
  }
  if (!(await providerMatchesAddress(target, normalized))) {
    return { ok: false, reason: 'wallet-not-found' };
  }
  try {
    await target.loginOrLink();
    return { ok: true, reason: 'linked' };
  } catch (err) {
    if (isWalletClaimedError(err)) {
      return { ok: false, reason: 'wallet-claimed' };
    }
    return { ok: false, reason: 'dismissed' };
  }
}

/**
 * High-level "sign in as the wagmi-active wallet" orchestration.
 *
 * Wraps {@link attemptLinkActiveWallet}-style logic with a session
 * switch: if the wagmi-active wallet isn't part of the current Privy
 * session, we logout from the current session first so the subsequent
 * `loginOrLink()` takes Privy's LOGIN path (not LINK).
 *
 * Why this exists: Privy enforces one-user-per-wallet at the API
 * level. Calling LINK on a wallet that already has its own Privy user
 * fails with "User already exists for this address" — a hard stop the
 * user can't resolve from the chat panel. The LOGIN path, by contrast,
 * transparently handles both cases:
 *   - Wallet has a Privy user → log in as that user.
 *   - Wallet has no Privy user → create one and log in.
 *
 * Trade-off: a wagmi switch from a wallet linked to one Privy session
 * to a wallet with its own Privy session WILL drop the previous
 * session's linked accounts (Twitter / Farcaster / email) for this
 * browser tab. Intentional — the chat use-case is "verify and chat as
 * the active wallet", and swapping sessions is the only robust way
 * under Privy's data model. Users who switch back to the original
 * wallet recover its session on the next sign-in click.
 *
 * Single SIWE signature in all cases:
 *   - already-linked → no signature
 *   - linkable (no conflict) → 1 signature on the LOGIN path
 *   - claimed by another Privy user → 1 signature on the LOGIN path
 *     (no failed LINK attempt because we logout first)
 */
export async function attemptSignInActiveWallet(deps: {
  walletsReady: boolean;
  authenticated: boolean;
  wagmiAddress: string | undefined;
  linkedAddresses: Set<string>;
  wallets: ReadonlyArray<LinkableWallet>;
  /**
   * Privy-session logout. Called only when authenticated AND the
   * wagmi-active wallet isn't already in the linked set. Must NOT
   * disconnect the wagmi connector — wagmi state has to survive so
   * the subsequent `loginOrLink()` can find the wallet entry and
   * the underlying provider can sign.
   */
  privyLogout: () => Promise<void>;
}): Promise<LinkActiveWalletResult> {
  if (!deps.walletsReady || !deps.wagmiAddress) {
    return { ok: false, reason: 'not-ready' };
  }
  const normalized = deps.wagmiAddress.toLowerCase();
  if (deps.authenticated && deps.linkedAddresses.has(normalized)) {
    return { ok: true, reason: 'already-linked' };
  }
  const target = deps.wallets.find((w) => w.address.toLowerCase() === normalized);
  if (!target) {
    return { ok: false, reason: 'wallet-not-found' };
  }
  if (!(await providerMatchesAddress(target, normalized))) {
    return { ok: false, reason: 'wallet-not-found' };
  }
  if (deps.authenticated) {
    await deps.privyLogout();
  }
  try {
    await target.loginOrLink();
    return { ok: true, reason: 'linked' };
  } catch (err) {
    // After logout, loginOrLink uses the LOGIN path which doesn't
    // surface "linked_to_another_user" — but defend the branch in
    // case Privy SDK behaviour changes.
    if (isWalletClaimedError(err)) {
      return { ok: false, reason: 'wallet-claimed' };
    }
    return { ok: false, reason: 'dismissed' };
  }
}
